import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FileText, Sparkles, Mail, Loader2, Shield, Database, Zap, Send } from 'lucide-react';

interface LoadingStage {
  label: string;
  duration: number;
  icon: typeof FileText;
}

const LOADING_STAGES: LoadingStage[] = [
  {
    label: 'Gathering intake data',
    duration: 3500,
    icon: FileText,
  },
  {
    label: 'Extracting client information',
    duration: 3500,
    icon: Database,
  },
  {
    label: 'Anonymizing personal data',
    duration: 4000,
    icon: Shield,
  },
  {
    label: 'Processing medical history',
    duration: 4500,
    icon: Sparkles,
  },
  {
    label: 'Formatting PUI request',
    duration: 4000,
    icon: FileText,
  },
  {
    label: 'Generating email content',
    duration: 4500,
    icon: Mail,
  },
  {
    label: 'Preparing carrier submissions',
    duration: 4000,
    icon: Zap,
  },
  {
    label: 'Finalizing request',
    duration: Infinity,
    icon: Loader2,
  },
];

interface PuiLoadingSequenceProps {
  isDataLoaded: boolean;
  onComplete: () => void;
}

const TOTAL_ESTIMATED_TIME = LOADING_STAGES.reduce(
  (acc, stage) => (stage.duration === Infinity ? acc : acc + stage.duration),
  0,
);

export function PuiLoadingSequence({ isDataLoaded, onComplete }: PuiLoadingSequenceProps) {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [completedStages, setCompletedStages] = useState<number[]>([]);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const hasCalledComplete = useRef(false);
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    const stage = LOADING_STAGES[currentStageIndex];
    const isFinalStage = currentStageIndex === LOADING_STAGES.length - 1;

    if (isFinalStage && !isAnimationComplete) {
      setIsAnimationComplete(true);
      setCompletedStages(prev => [...prev, currentStageIndex]);
      return;
    }

    if (stage.duration === Infinity) {
      return;
    }

    const timer = setTimeout(() => {
      setCompletedStages(prev => [...prev, currentStageIndex]);
      setCurrentStageIndex(prev => Math.min(prev + 1, LOADING_STAGES.length - 1));
    }, stage.duration);

    return () => clearTimeout(timer);
  }, [currentStageIndex, isAnimationComplete]);

  useEffect(() => {
    if (isAnimationComplete && isDataLoaded && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      const timer = setTimeout(onComplete, 1500);
      return () => clearTimeout(timer);
    }
  }, [isAnimationComplete, isDataLoaded, onComplete]);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTimeRef.current);
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const progress = ((currentStageIndex + 1) / LOADING_STAGES.length) * 100;
  const elapsedSeconds = Math.floor(elapsedTime / 1000);
  const estimatedSeconds = Math.ceil(TOTAL_ESTIMATED_TIME / 1000);
  const remainingSeconds = Math.max(0, estimatedSeconds - elapsedSeconds);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="flex min-h-[300px] items-center justify-center px-6 py-8">
      <div className="flex w-full flex-col items-center gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          className="flex flex-col items-center gap-2 text-center"
        >
          <h3 className="text-xl font-semibold text-gray-950">Generating anonymized PUI</h3>
          <p className="text-sm text-gray-600">
            Processing your request and preparing emails for carriers
          </p>
          <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
            <span>
              {isAnimationComplete ? 'Completing...' : `~${formatTime(remainingSeconds)} remaining`}
            </span>
          </div>
        </motion.div>

        <div className="flex flex-col items-center gap-3">
          <AnimatePresence initial={false}>
            {LOADING_STAGES.slice(0, Math.max(currentStageIndex + 1, completedStages.length)).map(
              (stage, index) => {
                const isCompleted = completedStages.includes(index);
                const isCurrent = currentStageIndex === index && !isCompleted;
                const Icon = stage.icon;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      transition: {
                        type: 'spring',
                        stiffness: 500,
                        damping: 30,
                        mass: 1,
                      },
                    }}
                    exit={{ opacity: 0, x: 20 }}
                    layout
                    transition={{
                      layout: {
                        type: 'spring',
                        stiffness: 500,
                        damping: 35,
                      },
                    }}
                    className={`flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium ${
                      isCompleted
                        ? 'bg-primary/10 text-primary'
                        : isCurrent
                          ? 'bg-primary/5 text-primary/80'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <AnimatePresence mode="wait">
                      {isCompleted ? (
                        <motion.svg
                          key="checkmark"
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{
                            opacity: 1,
                            scale: 1,
                            transition: {
                              type: 'spring',
                              stiffness: 500,
                              damping: 25,
                            },
                          }}
                          exit={{ opacity: 0, scale: 0 }}
                          className="size-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </motion.svg>
                      ) : (
                        <Icon
                          key="icon"
                          className={`size-5 ${isCurrent && Icon === Loader2 ? 'animate-spin' : ''}`}
                        />
                      )}
                    </AnimatePresence>
                    {stage.label}
                  </motion.div>
                );
              },
            )}
          </AnimatePresence>
        </div>

        <motion.div
          initial={{ opacity: 0, scaleX: 0.9 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
          className="w-full max-w-md"
        >
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <motion.div
              className="bg-primary h-full rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
